/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * listings-webapp. Mirrors the Valkyrie-app pattern documented in
 * `docs/orion-feature-flags.md` (creator-navigation PR #381):
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useIsRelatedEventsDenseAdsEnabled`).
 *
 * To add a new flag: append one key + one default to `ListingsWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useEffect, useMemo, useState } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';
import type { GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';

export type ListingsWebappSettings = {
  enable_increase_impressions_on_related_events: boolean;
  show_follow_venue_cta: boolean;
  show_follow_artist_cta: boolean;
  members_experiments_excluded_organizer_ids: string[];
  related_higher_price: boolean;
  enable_related_events_ad_prefetch: boolean;
  header_create_events_direct_signup: boolean;
  header_use_2025_layout: boolean;
  header_creator_cta_orange: boolean;
  header_organizer_create_events_cta: boolean;
  header_organizer_get_started_orange: boolean;
};

export type HeaderCreateEventsSettings = Pick<
  ListingsWebappSettings,
  | 'header_create_events_direct_signup'
  | 'header_use_2025_layout'
  | 'header_creator_cta_orange'
  | 'header_organizer_create_events_cta'
  | 'header_organizer_get_started_orange'
>;

export type HeaderCreatorVariant = NonNullable<GlobalHeaderProps['creatorVariant']>;

const HEADER_CREATE_EVENTS_SETTING_KEYS: Array<keyof HeaderCreateEventsSettings> = [
  'header_create_events_direct_signup',
  'header_use_2025_layout',
  'header_creator_cta_orange',
  'header_organizer_create_events_cta',
  'header_organizer_get_started_orange',
];

export const DEFAULT_SETTINGS = createAppDefaults<ListingsWebappSettings>({
  enable_increase_impressions_on_related_events: false,
  show_follow_venue_cta: false,
  show_follow_artist_cta: false,
  members_experiments_excluded_organizer_ids: [],
  related_higher_price: false,
  enable_related_events_ad_prefetch: false,
  header_create_events_direct_signup: false,
  header_use_2025_layout: false,
  header_creator_cta_orange: false,
  header_organizer_create_events_cta: false,
  header_organizer_get_started_orange: false,
});

export const useBspConfig = () =>
  useMemo(() => {
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';
    const environment = domain.includes('evbqa')
      ? 'staging'
      : domain.includes('localhost')
        ? 'development'
        : 'production';

    return {
      appName: 'listings-webapp',
      environment,
      defaultOrionSettings: DEFAULT_SETTINGS,
      defaultSegmentations: {},
    } as const;
  }, []);

/**
 * Gate for the denser related-events ad layout
 * (`enable_increase_impressions_on_related_events` Orion flag).
 *
 *   false (default / control) — request `slots: 2` and interleave ads via
 *     `interleaveArrays(promoted, organic)` (positions [1, 3]).
 *   true  (treatment)         — request `slots: 3` and interleave at
 *     positions [1, 2, 4] (ads → ads → organic → ads → organic).
 *
 * Same key + same default in eb-ui's `packages/attendee/listings/src/components/_shared`
 * and `packages/attendee/event-recommendations/src` so all three surfaces
 * ramp in lockstep.
 *
 * Excluded organizers: when `parentEventOrganizerId` is one of the ids in
 * the Orion setting `members_experiments_excluded_organizer_ids`, this
 * hook returns `false` regardless of the assigned segment — mirroring
 * the eb-ui checkout / event-recommendations gate so all three surfaces
 * opt the same events out in lockstep.
 */
export const useIsRelatedEventsDenseAdsEnabled = (parentEventOrganizerId?: string): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  const isOrganizerExcluded =
    !!parentEventOrganizerId && settings.members_experiments_excluded_organizer_ids.includes(parentEventOrganizerId);
  if (isOrganizerExcluded) {
    return false;
  }
  return settings.enable_increase_impressions_on_related_events;
};

/** Gate for the Follow Venue CTA (`show_follow_venue_cta` Orion flag). */
export const useShowFollowVenueCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_venue_cta;
};

/** Gate for the Follow Artist CTA (`show_follow_artist_cta` Orion flag). */
export const useShowFollowArtistCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_artist_cta;
};

/**
 * Gate for the higher-price related events experiment (`related_higher_price`
 * Orion flag). When on, the related-events request asks the search service to
 * zero out related events cheaper than the current event, so the carousel skews
 * towards equal-or-higher-priced events.
 *
 * Returns `isLoading` (unlike the other gates) so the related-events fetch can
 * wait for Orion to settle — otherwise a treatment visitor whose Orion resolves
 * after the fetch fires would be read as control and silently get the
 * unfiltered carousel, contaminating the experiment.
 */
export const useIsRelatedHigherPriceEnabled = (): { isEnabled: boolean; isLoading: boolean } => {
  let settings: Partial<ListingsWebappSettings> = {};
  let isLoading = false;
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {}
  return { isEnabled: settings.related_higher_price ?? false, isLoading };
};

/**
 * Gate for the related-events ad prefetch experiment (`enable_related_events_ad_prefetch` Orion flag).
 *
 * When on, the lazy-load observer for the related-events section gets a wider `rootMargin`, so the
 * promoted-events fetch fires earlier and the slot-1 ad card has time to mount before the visitor scrolls
 * into it. The two margins are `CONTROL_AD_PREFETCH_MARGIN_PX` / `TREATMENT_AD_PREFETCH_MARGIN_PX` in
 * `useRelatedAndPromotedEventsLazy`; `RelatedEvents` maps this boolean onto them.
 *
 * Why this is the lever: web `related_events` loses slot-1 impressions on ~170K sessions / 3 days — those sessions fire impressions on slots 2 and 3 but never slot 1, because the card mounts after the visitor has already scrolled past its position.
 * No other surface shows more than ~5% of this pattern.
 * The billable per-card beacon needs 25% of the card in viewport with no `rootMargin` of its own (`EventCardTracking`), so the card has to exist before the visitor arrives.
 *
 * Boolean rather than a numeric margin deliberately. Every multi-arm Orion experiment we run models its arms
 * as named string segments (see eb-ui checkout), and every on/off gate is a boolean — a raw number has no
 * precedent on the platform. The initiative also specifies a single treatment, not a set of arms, and the
 * treatment magnitude must not move mid-experiment anyway, so there is nothing to tune remotely.
 *
 * Deliberately does NOT return `isLoading`, unlike the higher-price and linear-score gates.
 * Those hold the fetch until Orion settles; holding the fetch here would defeat the treatment, whose entire content is "fetch earlier".
 * Instead the observer is rebuilt when the value settles (the options object is memoized on the margin, so a changed margin re-creates the observer).
 * The one-shot observer has not fired yet in the common case — the section sits far below the fold — so the treatment applies.
 * A visitor whose Orion settles after the section is already in range keeps control timing; that is the same tolerable-loss tradeoff documented on the dense-ads gate.
 */
export const useIsRelatedEventsAdPrefetchEnabled = (): boolean => {
  let settings: Partial<ListingsWebappSettings> = {};
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
  } catch {}
  return settings.enable_related_events_ad_prefetch ?? false;
};

/**
 * Header experiment settings are owned by Janus. Keep a treatment hidden until
 * Orion settles so missing, malformed, and SSR-only values preserve control.
 * Mirrors homepage-webapp's `resolveHeaderCreatorVariant` / `useHeaderCreateEventsSettings`
 * (see eb-web PR #299) so the same Janus settings drive both surfaces identically.
 */
export const resolveHeaderCreatorVariant = (
  rawSettings: unknown,
  isLoading: boolean,
  isServer = typeof window === 'undefined',
): HeaderCreatorVariant => {
  if (isLoading || isServer || !rawSettings || typeof rawSettings !== 'object') {
    return 'control';
  }

  const settings = rawSettings as Record<string, unknown>;
  if (
    HEADER_CREATE_EVENTS_SETTING_KEYS.some(
      (setting) => settings[setting] !== undefined && typeof settings[setting] !== 'boolean',
    )
  ) {
    return 'control';
  }

  const directSignup = settings.header_create_events_direct_signup === true;
  const use2025Layout = settings.header_use_2025_layout === true;
  const orangeCreatorCta = settings.header_creator_cta_orange === true;
  const organizerCreateEvents = settings.header_organizer_create_events_cta === true;
  const orangeGetStarted = settings.header_organizer_get_started_orange === true;

  if (directSignup && !use2025Layout && !orangeCreatorCta && !organizerCreateEvents && !orangeGetStarted) {
    return 'direct-signup';
  }

  if (!directSignup && use2025Layout && !orangeCreatorCta && organizerCreateEvents && !orangeGetStarted) {
    return 'restore-2025-layout';
  }

  if (directSignup && !use2025Layout && orangeCreatorCta && organizerCreateEvents && orangeGetStarted) {
    return 'orange-direct-signup';
  }

  return 'control';
};

export const useHeaderCreateEventsSettings = (): {
  settings: HeaderCreateEventsSettings;
  isLoading: boolean;
} => {
  let orionSettings: unknown = {};
  let isLoading = false;
  try {
    const state = useOrionSettings<ListingsWebappSettings>();
    orionSettings = state.settings;
    isLoading = state.isLoading;
  } catch {
    // SSR without a provider must retain the shared HTML control variant.
  }

  const settings = orionSettings as Record<string, unknown>;
  const hasMalformedSettings =
    !orionSettings ||
    typeof orionSettings !== 'object' ||
    HEADER_CREATE_EVENTS_SETTING_KEYS.some(
      (setting) => settings[setting] !== undefined && typeof settings[setting] !== 'boolean',
    );

  return {
    settings: {
      header_create_events_direct_signup:
        !isLoading && !hasMalformedSettings && settings.header_create_events_direct_signup === true,
      header_use_2025_layout: !isLoading && !hasMalformedSettings && settings.header_use_2025_layout === true,
      header_creator_cta_orange: !isLoading && !hasMalformedSettings && settings.header_creator_cta_orange === true,
      header_organizer_create_events_cta:
        !isLoading && !hasMalformedSettings && settings.header_organizer_create_events_cta === true,
      header_organizer_get_started_orange:
        !isLoading && !hasMalformedSettings && settings.header_organizer_get_started_orange === true,
    },
    isLoading,
  };
};

/**
 * Hydration-safe wrapper around `resolveHeaderCreatorVariant`.
 *
 * SSR renders `control` (via the `isServer` check), and the SSR HTML is
 * shared-cached. If Orion settings resolve before hydration (client cache),
 * the first client render would emit a treatment variant and mismatch the
 * server markup, tripping React's hydration warning and re-rendering the
 * header. Preserving control until after the first client render keeps the
 * hydrate markup identical to SSR; the treatment then applies on the next
 * render. Mirrors creator-mlps-webapp's `useHeaderCreatorVariant`
 * (eb-web PR #309, same bugbot finding).
 */
export const useHeaderCreatorVariant = (): HeaderCreatorVariant => {
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const { settings, isLoading } = useHeaderCreateEventsSettings();

  // Preserve the SSR control markup through the first client render.
  return resolveHeaderCreatorVariant(settings, isLoading, !hasHydrated);
};
